import type {
  ReactivationReceiptLabels,
  ReactivationReceiptRowViewDto,
  ReactivationReceiptViewDto,
  StoreConceptViewDto,
  SubscriptionBlockViewDto,
} from '@subscriptions/application/dtos';
import { STORE_CONCEPT_KEYS } from '@subscriptions/application/utils';
import { formatCurrencyMXN } from '@subscriptions/application/utils/formatCurrency';

const toLabel = (concept: StoreConceptViewDto, plan: string, labels: ReactivationReceiptLabels): string => {
  const base =
    concept.key === STORE_CONCEPT_KEYS.license
      ? labels.planName(plan || concept.name || '')
      : concept.name ?? labels.conceptName(concept.key) ?? concept.key;

  const withQuantity = (concept.quantity ?? 1) > 1 ? labels.withQuantity(base, concept.quantity as number) : base;

  return concept.periodStart ? labels.restOfCycle(withQuantity) : withQuantity;
};

/**
 * Turns the charges of a churned block into the receipt of its reactivation. Call it before paying:
 * once the block is active the API replaces these charges with the recurring ones.
 */
export const mapBlockToReactivationReceipt = (
  block: Pick<SubscriptionBlockViewDto, 'name' | 'stores'>,
  labels: ReactivationReceiptLabels,
): ReactivationReceiptViewDto => {
  const rows: ReactivationReceiptRowViewDto[] = block.stores.flatMap((store) =>
    store.concepts.map((concept) => ({
      key: `${store.storeUuid}-${concept.key}-${concept.name ?? ''}`,
      label: toLabel(concept, store.plan, labels),
      amountLabel: formatCurrencyMXN(concept.value),
    })),
  );

  const total = block.stores.reduce(
    (sum, store) => sum + store.concepts.reduce((storeSum, concept) => storeSum + concept.value, 0),
    0,
  );

  return {
    storeName: block.stores.length === 1 ? block.stores[0].storeName : block.name,
    totalLabel: formatCurrencyMXN(total),
    conceptCount: rows.length,
    rows,
  };
};
